import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Avatar } from "../components/Avatar.js";
import { Button, inputStyle, Notice } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { fetchFranchises } from "../lib/franchises.js";
import { lotLabel, lotWarnings } from "../lib/lot-warnings.js";
import {
  createLot,
  fetchPoolLots,
  fetchPublishedLots,
  placeLotPlayer,
  publishLots,
  removeLot,
  reorderLots,
  setLotName,
  setLotPrice,
  unpublishLots,
} from "../lib/lots.js";
import { fetchPoolSeeding } from "../lib/seeding.js";
import { moveBy } from "../lib/seeding-order.js";
import { type AuctionCurrency, formatPrice } from "../lib/currency.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { Lot, LotPlayer, Tournament, TournamentEvent } from "../lib/types.js";
import { PoolRows } from "./OrganiseRows.js";

/**
 * The control inside a row: a hairline frame, not the 2px one of a form field. A frame that heavy
 * on every player made the list a wall of boxes, and the names, which are what you read, lost to
 * the controls. The height stays 44px, because a phone is the usual way in.
 */
const rowControl = {
  ...inputStyle,
  border: "1px solid var(--color-line-muted)",
  minHeight: 44,
  fontSize: 14,
} as const;

/** A lot's name is its heading: display type on a rule, still a field when you tap it. */
const lotNameStyle = {
  width: "100%",
  minHeight: 44,
  padding: 0,
  background: "transparent",
  color: "var(--color-ink)",
  border: "none",
  borderBottom: "1px solid var(--color-line-muted)",
  borderRadius: 0,
  fontSize: 24,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: "0.01em",
} as const;

/**
 * An auction tournament's lots, on a page of their own like Teams and Seeding: for each pool, its
 * lots in auction order with their base prices and players, the bidding players still in none
 * with their seed averages as a guide, the warnings, and publishing the lots to the captains.
 * Reached from the tournament's Organise page.
 *
 * Organisers only, and only for an auction tournament. Both are signposting; the database
 * refuses the writes and sends nobody else a lot.
 */
export function TournamentLots() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });
  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);

  if (!hasRole(member, "organiser")) {
    return <Navigate to={`/tournaments/${tournamentId}`} replace />;
  }

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error
            ? "Couldn't load this tournament. Try again."
            : "That tournament isn't here any more."}
        </Notice>
      </main>
    );
  }

  if (!tournament.auction) {
    return <Navigate to={`/tournaments/${tournament.id}/manage`} replace />;
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link
        to={`/tournaments/${tournament.id}/manage`}
        className="micro mb-4 inline-block underline"
      >
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow={`Organise · ${tournament.name}`} title="Lots" />
      <p className="mb-6 text-sm text-ink-muted">
        Group each pool&rsquo;s bidding players into lots and set what each lot starts at. You place
        every player; the seed is only a guide. Captains see the lots once you publish them.
      </p>
      {/* One row per pool; each pool's lots is a page of its own under the event. */}
      <PoolRows tournament={tournament} step="lots" />
    </main>
  );
}

/**
 * A pool's published lots. The database hands the rows to organisers and the pool's captains
 * only, so an empty list means "nothing you may see" as much as "nothing yet", and both read
 * the same. Shared by the link rows and the page, so a tap from one to the other is cached.
 */
function usePublishedLots(event: TournamentEvent) {
  return useQuery({
    queryKey: ["lots-published", event.id],
    queryFn: () => fetchPublishedLots(event.id),
    enabled: event.lotsPublishedAt !== null,
  });
}

/**
 * The way to a pool's published lots, on the tournament page's Auction section and the
 * captain's page: one row with how many lots and players. Renders nothing before publishing and
 * for anybody the database does not hand the rows to, so it can sit there for everybody.
 */
export function LotsLinkRow({
  tournament,
  event,
}: {
  tournament: Tournament;
  event: TournamentEvent;
}) {
  const { data = [] } = usePublishedLots(event);
  if (event.lotsPublishedAt === null || data.length === 0) return null;

  const players = data.reduce((sum, lot) => sum + lot.players.length, 0);
  const facts = `${data.length} ${data.length === 1 ? "lot" : "lots"} · ${players} ${players === 1 ? "player" : "players"}`;

  return (
    <ul>
      <li className="border-b" style={{ borderColor: "var(--color-line-muted)" }}>
        <Link
          to={`/tournaments/${tournament.id}/lots/${event.id}`}
          className="flex min-h-[56px] items-center gap-3 py-2"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-base font-bold">Lots · {event.name}</span>
            <span className="font-mono text-[11px] text-ink-muted">{facts}</span>
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            aria-hidden
            className="shrink-0"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </li>
    </ul>
  );
}

/**
 * One pool's published lots, read-only, for its captains and the organisers: each lot in order
 * with its base price and players, as published. No sold marks and no prices paid: the
 * captain's page has those. Anybody else is told there are no lots to show.
 */
export function PoolLotsPage() {
  const { tournamentId, eventId } = useParams<{ tournamentId: string; eventId: string }>();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });
  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);
  const event = tournament?.events.find((candidate) => candidate.id === eventId);

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament || !event) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error ? "Couldn't load this pool. Try again." : "That pool isn't here any more."}
        </Notice>
      </main>
    );
  }

  if (!tournament.auction) {
    return <Navigate to={`/tournaments/${tournament.id}`} replace />;
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/tournaments/${tournament.id}`} className="micro mb-4 inline-block underline">
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow={`Lots · ${tournament.name}`} title={event.name} />
      <PublishedLots event={event} currency={tournament.auctionCurrency} />
    </main>
  );
}

function PublishedLots({ event, currency }: { event: TournamentEvent; currency: AuctionCurrency }) {
  const { data = [], isLoading } = usePublishedLots(event);

  if (isLoading) return <p className="micro">Loading…</p>;
  if (event.lotsPublishedAt === null || data.length === 0) {
    return <p className="text-sm text-ink-muted">No lots to show.</p>;
  }

  return (
    <section>
      <ol>
        {data.map((lot, index) => (
          <li
            key={lot.id}
            className="border-b py-3"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span
                className="font-display uppercase"
                style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}
              >
                {lotLabel(lot, index)}
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-muted">
                {lot.basePrice === null
                  ? "No base price"
                  : `Base ${formatPrice(lot.basePrice, currency)}`}
              </span>
            </div>
            <ul className="mt-1.5 flex flex-col gap-1">
              {lot.players.map((player) => (
                <li key={player.memberId} className="flex items-center gap-2 text-[15px]">
                  <Avatar name={player.displayName} url={player.avatarUrl} size={24} />
                  {player.displayName}
                </li>
              ))}
              {lot.players.length === 0 ? (
                <li className="text-sm text-ink-muted">Nobody yet.</li>
              ) : null}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function PoolLots({
  tournament,
  event,
}: {
  tournament: Tournament;
  event: TournamentEvent;
}) {
  const queryClient = useQueryClient();
  const started = tournament.auctionStartedAt !== null;
  const published = event.lotsPublishedAt !== null;

  const { data, isPending } = useQuery({
    queryKey: ["pool-lots", event.id],
    queryFn: () => fetchPoolLots(event.id, event.entries),
  });
  const { data: setup } = useQuery({
    queryKey: ["franchises", tournament.id],
    queryFn: () => fetchFranchises(tournament.id),
  });
  // Only a guide: the live average an organiser reads, when the panel has produced one.
  const { data: seeding } = useQuery({
    queryKey: ["pool-seeding", event.id],
    queryFn: () => fetchPoolSeeding(event.id),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["pool-lots", event.id] });
    void queryClient.invalidateQueries({ queryKey: ["lots-published", event.id] });
    void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
  };

  const create = useMutation({ mutationFn: () => createLot(event.id), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: (lotId: string) => removeLot(lotId),
    onSuccess: refresh,
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderLots(event.id, ids),
    onSuccess: refresh,
  });
  const place = useMutation({
    mutationFn: (args: { memberId: string; lotId: string | null }) =>
      placeLotPlayer(event.id, args.memberId, args.lotId),
    onSuccess: refresh,
  });
  const publish = useMutation({
    mutationFn: () => (published ? unpublishLots(event.id) : publishLots(event.id)),
    onSuccess: refresh,
  });

  const lots = data?.lots ?? [];
  const unassigned = data?.unassigned ?? [];
  // A player nobody on the panel ranked has no mean, and so no seed to show.
  const seeds = new Map(
    (seeding?.average ?? []).flatMap((entry) =>
      entry.mean === null ? [] : [[entry.memberId, entry.mean] as const],
    ),
  );

  const squads = (setup?.squads ?? []).filter((squad) => squad.eventId === event.id);
  const names = new Map((setup?.franchises ?? []).map((f) => [f.id, f.name]));
  const warnings = lotWarnings({
    lots,
    unassigned: unassigned.length,
    teams: squads.length,
    currency: tournament.auctionCurrency,
    purses: squads.flatMap((squad) =>
      squad.purse === null
        ? []
        : [
            {
              squadId: squad.id,
              name: names.get(squad.franchiseId) ?? "A team",
              purse: squad.purse,
            },
          ],
    ),
  });

  const error = create.error ?? remove.error ?? reorder.error ?? place.error ?? publish.error;
  const busy =
    create.isPending ||
    remove.isPending ||
    reorder.isPending ||
    place.isPending ||
    publish.isPending;

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="micro">{event.name}</h2>
        {published ? (
          <span
            className="font-mono px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ background: "var(--color-line)", color: "var(--color-ground)" }}
          >
            Published
          </span>
        ) : (
          <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-muted">
            Not published
          </span>
        )}
      </div>
      <p className="font-mono mb-4 text-[11px] text-ink-muted">
        {squads.length} {squads.length === 1 ? "team" : "teams"} · {unassigned.length} to place
      </p>

      {isPending ? (
        <p className="micro">Loading…</p>
      ) : (
        <>
          {warnings.length > 0 ? (
            <div
              className="mb-5 border-l-2 pl-3"
              style={{ borderColor: "var(--color-attention)" }}
              role="status"
            >
              <h3 className="micro" style={{ color: "var(--color-attention)" }}>
                To check · {warnings.length}
              </h3>
              <ul className="mt-1 flex flex-col gap-1 text-sm">
                {warnings.map((warning) => (
                  <li key={`${warning.kind}-${warning.lotId ?? warning.squadId ?? ""}`}>
                    {warning.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : lots.length > 0 ? (
            <p className="mb-4 text-sm text-ink-muted">Nothing to flag.</p>
          ) : null}

          <ol>
            {lots.map((lot, index) => (
              <LotCard
                key={lot.id}
                lot={lot}
                number={index + 1}
                teams={squads.length}
                lots={lots}
                seeds={seeds}
                currency={tournament.auctionCurrency}
                locked={started}
                busy={busy}
                canMoveUp={index > 0}
                canMoveDown={index < lots.length - 1}
                onMove={(delta) =>
                  reorder.mutate(
                    moveBy(
                      lots.map((l) => l.id),
                      index,
                      delta,
                    ),
                  )
                }
                onRemove={() => remove.mutate(lot.id)}
                onPlace={(memberId, lotId) => place.mutate({ memberId, lotId })}
                onPriced={refresh}
              />
            ))}
          </ol>

          {!started ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => create.mutate()}
              className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-1.5 border-[1.5px] border-dashed text-sm font-semibold text-ink-muted disabled:opacity-40"
              style={{ borderColor: "var(--color-ink-muted)" }}
            >
              <span aria-hidden>+</span> Add a lot
            </button>
          ) : null}

          {unassigned.length > 0 ? (
            <div className="mt-8">
              <h3 className="micro">Not in a lot</h3>
              <ul>
                {unassigned.map((player) => (
                  <PlayerRow
                    key={player.memberId}
                    player={player}
                    seed={seeds.get(player.memberId)}
                    lots={lots}
                    current={null}
                    disabled={started || busy}
                    onPlace={(lotId) => place.mutate({ memberId: player.memberId, lotId })}
                  />
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-3">
            {!started ? (
              <Button
                type="button"
                tone={published ? "line" : "primary"}
                disabled={busy}
                onClick={() => publish.mutate()}
              >
                {published ? "Unpublish lots" : "Publish lots to captains"}
              </Button>
            ) : (
              <Notice>The auction has started, so the lots are fixed.</Notice>
            )}
          </div>

          {error ? <Notice tone="attention">{error.message}</Notice> : null}
        </>
      )}
    </section>
  );
}

function LotCard({
  lot,
  number,
  teams,
  lots,
  seeds,
  currency,
  locked,
  busy,
  canMoveUp,
  canMoveDown,
  onMove,
  onRemove,
  onPlace,
  onPriced,
}: {
  lot: Lot;
  number: number;
  teams: number;
  lots: Lot[];
  seeds: Map<string, number>;
  currency: AuctionCurrency;
  locked: boolean;
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  onPlace: (memberId: string, lotId: string | null) => void;
  onPriced: () => void;
}) {
  const perTeam =
    teams > 0 && lot.players.length > 0 && lot.players.length % teams === 0
      ? ` · ${lot.players.length / teams} per team`
      : "";

  return (
    <li
      className="border-t pb-2 pt-5 first:border-t-0 first:pt-0"
      style={{ borderColor: "var(--color-line-muted)" }}
    >
      <div className="flex items-center gap-1">
        <LotName lot={lot} number={number} locked={locked} onSaved={onPriced} />
        {!locked ? (
          <>
            <MoveButton
              label={`Move lot ${number} up`}
              disabled={!canMoveUp || busy}
              onClick={() => onMove(-1)}
            >
              <path d="M6 15l6-6 6 6" />
            </MoveButton>
            <MoveButton
              label={`Move lot ${number} down`}
              disabled={!canMoveDown || busy}
              onClick={() => onMove(1)}
            >
              <path d="M6 9l6 6 6-6" />
            </MoveButton>
            <Button
              type="button"
              tone="quiet"
              disabled={busy}
              aria-label={`Remove lot ${number}`}
              onClick={onRemove}
            >
              Remove
            </Button>
          </>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-3">
        <LotPrice
          lot={lot}
          number={number}
          currency={currency}
          locked={locked}
          onSaved={onPriced}
        />
        <span className="font-mono ml-auto text-right text-[11px] text-ink-muted">
          {lot.players.length} {lot.players.length === 1 ? "player" : "players"}
          {perTeam}
        </span>
      </div>

      <ul className="mt-2">
        {lot.players.map((player) => (
          <PlayerRow
            key={player.memberId}
            player={player}
            seed={seeds.get(player.memberId)}
            lots={lots}
            current={lot.id}
            disabled={locked || busy}
            onPlace={(lotId) => onPlace(player.memberId, lotId)}
          />
        ))}
      </ul>
      {lot.players.length === 0 ? <p className="mt-1 text-sm text-ink-muted">Nobody yet.</p> : null}
    </li>
  );
}

/**
 * A lot's name, as its heading. Saved on leaving the field or Enter; clearing it leaves the lot
 * shown by its number, which is also the placeholder, so an unnamed lot reads "Lot 2".
 */
function LotName({
  lot,
  number,
  locked,
  onSaved,
}: {
  lot: Lot;
  number: number;
  locked: boolean;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(lot.name ?? "");
  const save = useMutation({
    mutationFn: (name: string | null) => setLotName(lot.id, name),
    onSuccess: onSaved,
  });

  const commit = () => {
    const name = value.trim() === "" ? null : value.trim();
    if (name !== lot.name) save.mutate(name);
  };

  return (
    <span className="flex min-w-0 flex-1 flex-col">
      <input
        aria-label={`Name, lot ${number}`}
        placeholder={`Lot ${number}`}
        maxLength={40}
        value={value}
        disabled={locked}
        onChange={(event) => setValue(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
        }}
        className="font-display uppercase placeholder:text-ink-muted disabled:opacity-40"
        style={lotNameStyle}
      />
      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
    </span>
  );
}

/** A lot's base price, a whole number in the tournament's currency: digits only, saved on leaving the field or Enter. */
function LotPrice({
  lot,
  number,
  currency,
  locked,
  onSaved,
}: {
  lot: Lot;
  number: number;
  currency: AuctionCurrency;
  locked: boolean;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(lot.basePrice === null ? "" : String(lot.basePrice));
  const save = useMutation({
    mutationFn: (price: number | null) => setLotPrice(lot.id, price),
    onSuccess: onSaved,
  });

  const commit = () => {
    const price = value === "" ? null : Number(value);
    if (price !== lot.basePrice) save.mutate(price);
  };

  return (
    <span className="flex flex-col">
      <span className="flex items-center gap-2">
        <span className="micro" aria-hidden>
          Base price
        </span>
        <input
          aria-label={`Base price, lot ${number}`}
          placeholder="None"
          inputMode="numeric"
          value={value}
          disabled={locked}
          onChange={(event) => setValue(event.target.value.replace(/[^0-9]/g, ""))}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
          }}
          className="font-mono disabled:opacity-40"
          // An explicit width: `inputStyle` says 100%, which beats any width class and made the
          // field fill the row.
          style={{ ...rowControl, width: "5.5rem", textAlign: "right" }}
        />
        <span className="micro" aria-hidden>
          {currency}
        </span>
      </span>
      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
    </span>
  );
}

/**
 * One player, with their seed as a guide and a control to put them in a lot, move them to
 * another, or take them out. A select, not a drag, so it works on a phone.
 */
function PlayerRow({
  player,
  seed,
  lots,
  current,
  disabled,
  onPlace,
}: {
  player: LotPlayer;
  seed: number | undefined;
  lots: Lot[];
  current: string | null;
  disabled: boolean;
  onPlace: (lotId: string | null) => void;
}) {
  return (
    <li
      className="flex min-h-[52px] items-center gap-3 border-b py-1.5"
      style={{ borderColor: "var(--color-line-muted)" }}
    >
      <Avatar name={player.displayName} url={player.avatarUrl} size={28} />
      <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">
        {player.displayName}
      </span>
      {seed !== undefined ? (
        <span
          className="font-mono shrink-0 whitespace-nowrap text-[11px] text-ink-muted"
          title="Average seed position, a guide only"
        >
          seed {seed.toFixed(1)}
        </span>
      ) : null}
      <select
        aria-label={`Lot for ${player.displayName}`}
        value={current ?? ""}
        disabled={disabled}
        onChange={(event) => onPlace(event.target.value || null)}
        className="shrink-0 disabled:opacity-40"
        // Fixed, for the same reason: at 100% the select took the whole row and squeezed the
        // player's name to nothing.
        style={{ ...rowControl, width: "8rem" }}
      >
        <option value="">{current === null ? "Place in lot…" : "Take out of lot"}</option>
        {lots.map((lot, index) => (
          <option key={lot.id} value={lot.id}>
            {lotLabel(lot, index)}
          </option>
        ))}
      </select>
    </li>
  );
}

function MoveButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-11 items-center justify-center disabled:opacity-30"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        aria-hidden
      >
        {children}
      </svg>
    </button>
  );
}
